import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Row, Col, Toast, ToastContainer } from "react-bootstrap";
import { IActionHandler, AGUserInfo, ApproversInfo, ApproversList, DeviceBase } from "@preveil-api";
import {
  ExpressApprovalGroupSteps, useAppDispatch, Message, QueryParamKeys, instanceofApproversList, AccountErrorMessages, MessageHandlerDisplayType, normalizeQueryUserId, GlobalErrorMessages, MessageAnchors,
  KeyExchangeManager, Helpers, combine, AppUserKey, KeyStorageData, KeyStorageAPI, AccountIdentifiers, usePostUsersDevicesKeysMutation, useGetUsersDevicesKeysMutation, useDeleteUsersDevicesKeysByDeviceIdMutation,
  account_types, useAppSelector, PublicRoutes, useAuthContext
} from "src/common";
import { ExpressGetShard, Loading } from "src/components";
import { ErrorPanel, DefaultPanel, RecoveryPanel, CreateNewPasswordForm } from ".";
import { accountActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import recovery_img from "src/assets/images/recovery_group.svg";

type RecoveryExchangeBundle = {
  manager: KeyExchangeManager;
  transfer: { device_req: string; message: string; signature: string; };
};

type ApproverDeviceSignature = {
  signature: string;
  user_id: string;
  user_key_version: number;
};

function ExpressApprovalGroup() {
  const navigate = useNavigate();
  const [search_params, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const account_ids = useAppSelector((state: RootState) => state.account.account_ids);
  const redirect_url = useAppSelector((state: RootState) => state.ui.redirect_url);
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const [step, setStep] = useState<number>(ExpressApprovalGroupSteps.LOADING);
  const [user_info, setUserInfo] = useState<AGUserInfo>();
  const [approvers_list, setApproversList] = useState<ApproversList>();
  const [active_approver, setActiveApprover] = useState<ApproversInfo>();
  const [shards, setShards] = useState<string[]>([]);
  const [user_key, setUserKey] = useState<AppUserKey>();
  const [show, setShow] = useState<boolean>(false);
  const [recovery_exchange_bundle, setRecoveryExchangeBundle] = useState<RecoveryExchangeBundle | undefined>(undefined);
  const [approver_device_signatures, setApproverDeviceSignatures] = useState<ApproverDeviceSignature[]>([]);
  const [deleteDevice] = useDeleteUsersDevicesKeysByDeviceIdMutation();
  const [getDevices] = useGetUsersDevicesKeysMutation();
  const [addDevice] = usePostUsersDevicesKeysMutation();
  const { loginComplete } = useAuthContext();

  // First onload event
  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    handleInitApprovalGroup();
  }, []);

  // Description: Complete step after KSS User creds
  useEffect(() => {
    !!account_ids && dispatch(accountActions.findCollectionServerCurrentUser(account_ids));
  }, [account_ids]);

  // Description: Update the login status when current account is set in state
  useEffect(() => {
    (!!current_account || !!redirect_url) &&
      loginComplete(current_account, account_types.express, redirect_url);
  }, [current_account, redirect_url]);

  // Description: Block user from leaving
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
  };

  // Description: Get user ID and secret from URL. Set step and dispatch error if missing.
  async function handleInitApprovalGroup() {
    const query = search_params.get(QueryParamKeys.USER_ID_QUERY_KEY);
    const user_id = normalizeQueryUserId(query);
    const secret = search_params.get(QueryParamKeys.SECRET_QUERY_KEY)?.trim();
    if (!!user_id && !!secret) {
      !!user_id && setUserInfo({ user_id, secret });
      setStep(ExpressApprovalGroupSteps.SUBMIT_ACKNOWLEDGE_INFO);
    } else {
      setStep(ExpressApprovalGroupSteps.PAGE_ERROR);
      dispatch(uiActions.handleRequestErrors(
        new Message(AccountErrorMessages.approval_group_qs_stack_error, MessageHandlerDisplayType.logger),
        {
          error: AccountErrorMessages.approval_group_qs_stack_error,
          user_id,
          secret
        }
      ));
    };

    // Remove params
    if (!!search_params.toString()) {
      !!query && search_params.delete(QueryParamKeys.USER_ID_QUERY_KEY);
      !!secret && search_params.delete(QueryParamKeys.SECRET_QUERY_KEY);
      setSearchParams(search_params);
    }
  }

  // Description: Recover express user with new password and auth token
  async function recoverExpressUser(auth_token: string, pass: string, serialized_request: string, approver_signatures: ApproverDeviceSignature[], recovery_manager: KeyExchangeManager | undefined) 
  {
    const proposed = recovery_manager?.proposed_device;
    if (!proposed) {
      dispatch(uiActions.handleRequestErrors(
        new Message(AccountErrorMessages.restore_account_error, MessageHandlerDisplayType.logger),
        { error: "recovery_device_missing" }
      ));
      return;
    }
    const device_key = proposed.device_key;
    const kss_device: DeviceBase = {
      device_id: proposed.id,
      device_name: proposed.name,
      metadata: {},
      platform: "express",
      public_key: Helpers.b64Encode(device_key.public_user_key.serialize()),
    };
    if (!!user_info?.user_id && !!user_key) {
      const account_ids = {
        user_id: user_info.user_id,
        user_key,
        device_id: kss_device.device_id,
        device_key,
        user_key_serialized: Helpers.b64Encode(user_key.serialize()),
        device_key_serialized: Helpers.b64Encode(device_key.serialize()),
      };
      const wrapped_data = await KeyStorageData.wrapKSSData(pass, user_key, device_key, 1, account_ids.device_id);
      KeyStorageAPI.rekeyUser(account_ids, undefined, wrapped_data, undefined, Helpers.b64Encode(user_key.public_user_key.verify_key.public_key), auth_token).then((response) => {
        if (!response.isError) {
          addNewDevice(account_ids, kss_device, auth_token, serialized_request, approver_signatures);
        } else {
          const message = response.data.bad_parameter === AccountErrorMessages.previous_password_existed ? AccountErrorMessages.cannot_user_previous_password : 
            response.data.error === "no_sign_key" ? AccountErrorMessages.verify_key_not_found : AccountErrorMessages.error_changing_password;
          dispatch(uiActions.handleRequestErrors(new Message(message), response.data));
          if (response.data.bad_parameter !== AccountErrorMessages.previous_password_existed) {
            ApprovalGroupRequests.handleResetLogin();
          }
        }
      }).catch((error) => {
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.default), error));
        ApprovalGroupRequests.handleResetLogin();
      });
    }
  }

  /* Description: Adds a new device since we needed to create a new one in order to recover the account */
  function addNewDevice(_account_ids: AccountIdentifiers, device: DeviceBase, auth_token: string, serialized_request: string, approver_signatures: ApproverDeviceSignature[]) 
  {
    if (!!user_info?.user_id) {
      const request = {
        account_ids: _account_ids,
        body: {
          approver_signatures,
          serialized_request,
          user_id: user_info.user_id,
        },
      };
      addDevice(request).then(() => {
        deviceCleanUp(_account_ids, device, auth_token);
      }).catch((error: any) => {
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.restore_account_error), error));
        ApprovalGroupRequests.handleResetLogin();
      });
    }
  }

  /* Description: After creating a new device key, we must delete the other devices 
  we first get all the devices and delete the ones that are not the current one. */
  function deviceCleanUp(_account_ids: AccountIdentifiers, device: DeviceBase, auth_token: string) {
    const request = {
      account_ids: _account_ids,
      body: {
        userId: _account_ids.user_id,
      },
    };
    getDevices(request).then((response: any) => {
      const non_current_devices = response.data.devices.filter((_device: any) => _device.device_id !== device.device_id);
      if (non_current_devices.length === 0) {
        login(_account_ids, auth_token);
        return;
      }
      const delete_requests = non_current_devices.map((_device: any) => {
        const delete_request = {
          account_ids: _account_ids,
          body: {
            user_id: _account_ids.user_id,
            device_id: _device.device_id,
          },
        };
        // NOTE: Swallow individual failures so one bad delete doesn't reject the whole batch
        return deleteDevice(delete_request).catch((error: any) => {
          dispatch(uiActions.handleRequestErrors(new Message("Error Deleting Device", MessageHandlerDisplayType.logger), error));
        });
      });
      // NOTE: Only log in once every delete has settled, regardless of resolution order
      Promise.all(delete_requests).then(() => {
        login(_account_ids, auth_token);
      });
    }).catch((error: any) => {
      dispatch(uiActions.handleRequestErrors(new Message("Error Deleting Devices", MessageHandlerDisplayType.logger), error));
      login(_account_ids, auth_token);
    });
  }

  function login(_account_ids: AccountIdentifiers, auth_token: string) {
    dispatch((accountActions.handleSetAuthToken(auth_token)));
    dispatch(accountActions.getKssUserSuccess(_account_ids));
  }

  const ApprovalGroupRequests = {
    handleResetForms: () => {
      setStep(ExpressApprovalGroupSteps.SUBMIT_ACKNOWLEDGE_INFO);
      setApproversList(undefined);
      setActiveApprover(undefined);
      setRecoveryExchangeBundle(undefined);
      setApproverDeviceSignatures([]);
    },
    handleSubmitAcknowledgeInformation: (params: ApproversList) => {
      if (instanceofApproversList(params)) {
        setApproversList(params); // Validated in the control
        setApproverDeviceSignatures([]);
        setShow(true);
        setStep(ExpressApprovalGroupSteps.PROCESS_APPROVERS);
        if (!!user_info?.user_id) {
          const recovery_user_id = user_info.user_id;
          void (async () => {
            try {
              const recovery_manager = KeyExchangeManager.initKeyExchangeManager();
              const recovery_transfer = await recovery_manager.constructSerializedRecoveryData(recovery_user_id);
              setRecoveryExchangeBundle({ manager: recovery_manager, transfer: recovery_transfer });
            } catch (error: unknown) {
              dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.default), error));
            }
          })();
        }
      } else {
        ApprovalGroupRequests.handlePageError({
          message: GlobalErrorMessages.default,
          stack: {
            error: "Error in the type returned from get user info call",
            params
          }
        });
      }
    },
    handleGetShard: (approver: ApproversInfo) => {
      setActiveApprover(approver);
    },
    handlePageSuccess: (params: { approver_id: string; approver_key_version?: number; approver_signature?: string; new_shard: string }) => {
      if (!!params.approver_id && !!approvers_list) {
        // Set the active approver to approved
        const updated_approvers_list = {
          ...approvers_list,
          approvers: approvers_list.approvers.map(approver =>
            approver.user_id === params.approver_id
              ? { ...approver, approved: true }
              : approver
          )
        };
        // Only add shard if it doesn't exist in the shards array to avoid duplicates
        setShards(prev_shards => prev_shards.includes(params.new_shard) ? prev_shards : [...prev_shards, params.new_shard]);
        setApproversList(updated_approvers_list);
        if (!!params.approver_signature && params.approver_key_version !== undefined) {
          setApproverDeviceSignatures(prev => {
            const next_entry: ApproverDeviceSignature = {
              signature: params.approver_signature as string,
              user_id: params.approver_id,
              user_key_version: params.approver_key_version as number,
            };
            const without_duplicate = prev.filter(entry => entry.user_id !== params.approver_id);
            return [...without_duplicate, next_entry];
          });
        }
      }
      setActiveApprover(undefined);
      setStep(ExpressApprovalGroupSteps.PROCESS_APPROVERS);
    },
    handleReconstructUserKey: async () => {
      const optional_shard = combine(shards); // All approvers are optional
      const user_key_string = combine([optional_shard]); // Supposed to combine with the required_shard but we only have optional shard
      const key_exchange_manager = KeyExchangeManager.initKeyExchangeManager();
      const user_key = await key_exchange_manager.getDeserializeUserKey(user_key_string);
      setUserKey(user_key);
      setStep(ExpressApprovalGroupSteps.CREATE_NEW_PASSWORD);
    },
    handleSubmitNewPassword: async (params: { password: string }) => {
      if (!!user_key && !!user_info?.user_id) {
        const token = await KeyStorageData.authenticationToken(user_info.user_id, params.password);
        const auth_token = Helpers.b64Encode(token);
        const serialized_request = recovery_exchange_bundle?.transfer.device_req ?? "";
        recoverExpressUser(auth_token, params.password, serialized_request, approver_device_signatures, recovery_exchange_bundle?.manager);
      }
    },
    // Description: Reset all form states and go back to Login page
    handleResetLogin: () => {
      setUserInfo(undefined);
      setShards([]);
      setUserKey(undefined);
      setRecoveryExchangeBundle(undefined);
      setApproverDeviceSignatures([]);
      navigate(`/${PublicRoutes.login_route}`); // Redirect to login page
    },
    handleDestroy: () => {
      setActiveApprover(undefined);
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
      setStep(ExpressApprovalGroupSteps.PAGE_ERROR);
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((ApprovalGroupRequests as any)[callback] instanceof Function) {
      (ApprovalGroupRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      ApprovalGroupRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  const ToastWarning = () => {
    return <ToastContainer
      className="dirty-warning"
      position="bottom-start" >
      <Toast bg="warning" show={show} onClose={() => setShow(false)}>
        <Toast.Header>Leave Browser Open</Toast.Header>
        <Toast.Body>
          Reloading your browser may result in cancelling your process and you will need to request recovery again.
        </Toast.Body>
      </Toast>
    </ToastContainer>;
  };

  const ApproveGroupPanels = () => {
    switch (step) {
      case ExpressApprovalGroupSteps.PAGE_ERROR: // {/* state === FAILED_STATE">
        return <ErrorPanel user_id={user_info?.user_id} />;
      case ExpressApprovalGroupSteps.SUBMIT_ACKNOWLEDGE_INFO:
      return !!user_info ?
        <DefaultPanel handleAction={handlePageActions} user_info={user_info} /> :
        <ErrorPanel />;
      case ExpressApprovalGroupSteps.PROCESS_APPROVERS:
        return !!user_info && !!approvers_list ?
          <RecoveryPanel
            approvers_list={approvers_list}
            user_info={user_info}
            handleAction={handlePageActions} /> :
          <ErrorPanel user_id={user_info?.user_id} />;
      case ExpressApprovalGroupSteps.CREATE_NEW_PASSWORD:
        return !!user_key ?
          <CreateNewPasswordForm
            handleAction={handlePageActions}
            user_identifier={!!user_info ? { user_id: user_info.user_id } : undefined}
          /> :
          <ErrorPanel user_id={user_info?.user_id} />;
      default:
        return <p>Loading...</p>;
    };
  };

  return <>
    <ToastWarning />
    {!!active_approver && !!user_info && !!recovery_exchange_bundle ?
      <ExpressGetShard
        approver_user_id={active_approver.user_id}
        handleAction={handlePageActions}
        recovery_key_exchange_manager={recovery_exchange_bundle.manager}
        recovery_key_transfer={recovery_exchange_bundle.transfer}
        user_id={user_info.user_id}
      /> :
      !!active_approver && !!user_info ?
        <Row className="justify-content-center mt-4">
          <Col md={8}>
            <Loading className="in-place" />
          </Col>
        </Row> :
      <Row className="justify-content-center mt-4">
        <Col md={8}>
          <h1 className="main-header">
            {
              step === ExpressApprovalGroupSteps.PAGE_ERROR ? "Recovery Group Not Set" : "Reset Your Password"
            }
          </h1>
          <Card className="pv-card">
            {
              step === ExpressApprovalGroupSteps.SUBMIT_ACKNOWLEDGE_INFO &&
              <Card.Img variant="top" src={recovery_img} className="py-3" />
            }
            <Card.Body className="p-4">
              {ApproveGroupPanels()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    }
  </>;
}

export default React.memo(ExpressApprovalGroup);
